import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Camera, ChevronLeft, X, Check } from 'lucide-react';
import { checkupAblaufAPI, checkupTabletAPI, checkupUebungAPI } from '@/lib/api';
import Unterschrift from '@/components/Unterschrift';
import ErgebnisSeite from '@/components/checkup/ErgebnisSeite';
import WegSeite from '@/components/checkup/WegSeite';
// Testimonial-Frage (W1.7) am Ende: Karte des Pakets w1-testimonial über `client.testimonial` (ohne: nichts, z. B. am Tablet)
import TestimonialFrage from '@/components/checkup/TestimonialAmEnde';
import { testimonialCrm } from '@/lib/testimonialClient';

/* Stepper „Check-up abschließen“ (Kundenakte Schritt 3+4, 28.10.2026; Bauauftrag docs/briefs/crm-kundenakte-checkup-angebot-v1.md,
   Abschnitt 13 geht vor). EINE Komponente für das Studio-Tablet (Geräte-Link, P1-Hellmodus wie bisher am Tablet) und das CRM
   (Vollbild-Panel über der Lead-Ansicht, Farben aus .crm-neu) — nur der API-Client ist ein anderer (`tabletClient`/`crmClient`).
   Reihenfolge: Einwilligung → Messung (Waage + von Hand) → Analyse (Δ zum letzten Check-up, vom Server) → Fragen → Befund →
   Kurse → Empfehlung (Angebotskarten) → Abschluss (Jetzt buchen | Angebot senden | Später | Kein Bedarf). Der Stand kommt immer
   vom Server; gerechnet wird hier nichts (auch kein Preis). Touch-Flächen ≥ 44 px, keine Browser-Dialoge. */

const ROT = '#E60000';
const SCHRITTE = [['einwilligung', 'Einwilligung'], ['messung', 'Messung'], ['analyse', 'Analyse'], ['fragen', 'Fragen'], ['befund', 'Befund'],
  ['kurse', 'Kurse'], ['empfehlung', 'Empfehlung'], ['abschluss', 'Abschluss']];
const AUSGAENGE = [['buchen', 'Jetzt buchen'], ['senden', 'Angebot senden'], ['spaeter', 'Später'], ['kein_bedarf', 'Kein Bedarf']];
const GRUENDE_JE_AUSGANG = { spaeter: ['ueberlegen', 'zeit', 'preis', 'sonstiges'], kein_bedarf: ['kein_bedarf', 'preis', 'zeit', 'sonstiges'] };
export const terminKey = (t) => `${t.kurs_id}:${t.wochentag}:${t.start}`;
export const tagKurz = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
export const heuteIso = (plusTage = 0) => { const d = new Date(); d.setDate(d.getDate() + plusTage); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
export const fehlerText = (e) => e?.response?.data?.detail || 'Das hat nicht geklappt. Bitte noch einmal versuchen.';
const zahl = (v, st = 1) => (v == null ? '–' : String(Number(v).toFixed(st)).replace('.', ','));
const euro = (v) => (v == null ? '–' : `${Number.isInteger(Number(v)) ? Number(v) : Number(v).toFixed(2).replace('.', ',')} €`);

/* Akzent: am Tablet das P1-Rot wie bisher, im CRM die Markenfarbe aus .crm-neu */
const AKZENT_TABLET = { className: 'text-white border-transparent', style: { background: ROT }, rahmen: '', rahmenStyle: { borderColor: ROT } };
const AKZENT_CRM = { className: 'bg-marke text-white border-transparent', style: undefined, rahmen: 'border-marke', rahmenStyle: undefined };
const Akzent = createContext(AKZENT_TABLET);

export function Knopf({ rot, children, ...p }) {
  const ak = useContext(Akzent);
  const basis = 'min-h-[56px] px-6 rounded-xl text-base font-semibold border border-border active:opacity-80 disabled:opacity-50';
  return <button type="button" {...p} className={`${basis} ${rot ? ak.className : ''} ${p.className || ''}`} style={rot ? ak.style : undefined}>{children}</button>;
}

function Chip({ an, children, ...p }) {
  const ak = useContext(Akzent);
  return (
    <button type="button" aria-pressed={an} {...p}
      className={`min-h-[52px] px-5 rounded-xl border text-base ${an ? `${ak.className} font-semibold` : 'border-border'} ${p.className || ''}`}
      style={an ? ak.style : undefined}>{children}</button>
  );
}

function sichtbar(frage, antworten) {
  if (!frage.nur_wenn) return true;
  return Object.entries(frage.nur_wenn).every(([k, werte]) => {
    const a = antworten[k];
    return Array.isArray(a) ? a.some((x) => werte.includes(x)) : werte.includes(a);
  });
}

/* Ergebnis- und Abschluss-Check-up (W1.6/W1.6b): Schritt 3 heißt dort „Ergebnis“ bzw. „Dein Weg“ */
const SCHRITT_LABEL = { ergebnis: { empfehlung: 'Ergebnis' }, abschluss: { empfehlung: 'Dein Weg' } };
const MIT_ERGEBNIS = ['ergebnis', 'abschluss'];

function Fortschritt({ schritt, art }) {
  const ak = useContext(Akzent);
  const i = schritt === 'fertig' ? SCHRITTE.length - 1 : Math.max(0, SCHRITTE.findIndex(([k]) => k === schritt));
  return (
    <div className="flex gap-1" aria-label="Fortschritt" data-testid="checkup-fortschritt">
      {SCHRITTE.map(([k, l0]) => [k, (SCHRITT_LABEL[art] || {})[k] || l0]).map(([k, l], n) => (
        <div key={k} className="flex-1 min-w-0">
          <div className={`h-2 rounded-full ${n <= i ? ak.className : ''}`} style={n <= i ? ak.style : { background: 'rgba(128,128,128,.3)' }} />
          <p className={`text-[11px] mt-1 text-center truncate ${n === i ? 'font-bold' : 'text-muted-foreground'}`}>{l}</p>
        </div>
      ))}
    </div>
  );
}

/* Kamera hinten, jsQR; nach dem ersten Treffer ist die Kamera aus und der Code geht an den Server. */
export function Scanner({ onCode, onAbbruch }) {   // auch auf /waage (WaagePage.js)
  const videoRef = useRef(null);
  const [fehler, setFehler] = useState('');
  useEffect(() => {
    let stream; let raf; let fertig = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      } catch { setFehler('Die Kamera ist nicht verfügbar. Bitte den Zugriff erlauben.'); return; }
      if (fertig) { stream.getTracks().forEach((t) => t.stop()); return; }
      const video = videoRef.current;
      video.srcObject = stream;
      try { await video.play(); } catch { /* egal */ }
      const { default: jsQR } = await import('jsqr');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const tick = () => {
        if (fertig) return;
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth; canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          const bild = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const qr = jsQR(bild.data, bild.width, bild.height, { inversionAttempts: 'dontInvert' });
          if (qr?.data) { fertig = true; stream.getTracks().forEach((t) => t.stop()); onCode(qr.data); return; }
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    })();
    return () => { fertig = true; if (raf) cancelAnimationFrame(raf); if (stream) stream.getTracks().forEach((t) => t.stop()); };
  }, [onCode]);
  return (
    <div className="space-y-3">
      {fehler ? <p className="text-red-600 text-base">{fehler}</p> : <video ref={videoRef} playsInline muted className="w-full max-h-[55vh] rounded-xl bg-black object-cover" />}
      <Knopf onClick={onAbbruch}>Abbrechen</Knopf>
    </div>
  );
}

function Frage({ f, wert, setWert }) {
  const w = wert ?? (f.art === 'mehrere' ? [] : '');
  return (
    <div className="space-y-2" data-testid={`frage-${f.key}`}>
      <p className="text-lg font-semibold">{f.frage}{f.pflicht ? ' *' : ''}</p>
      {f.hinweis && <p className="text-sm text-muted-foreground">{f.hinweis}</p>}
      {(f.art === 'eine' || f.art === 'mehrere') && (
        <div className="flex flex-wrap gap-2">
          {f.optionen.map((o) => {
            const an = f.art === 'eine' ? w === o.wert : w.includes(o.wert);
            return <Chip key={o.wert} an={an} onClick={() => setWert(f.art === 'eine' ? o.wert : (an ? w.filter((x) => x !== o.wert) : [...w, o.wert]))}>{o.label}</Chip>;
          })}
        </div>
      )}
      {f.art === 'text' && <textarea value={w} onChange={(e) => setWert(e.target.value)} rows={3} className="w-full rounded-xl border border-border bg-background p-3 text-base" />}
      {f.art === 'datum' && <input type="date" value={w} onChange={(e) => setWert(e.target.value)} className="min-h-[52px] rounded-xl border border-border bg-background px-3 text-base" />}
    </div>
  );
}

/* Messwerte mit Δ zum letzten Check-up — Wert, Δ und Wertung kommen vom Server (gut/schlecht nach dem Hauptziel). */
const WERTUNG_FARBE = { gut: 'text-emerald-600', schlecht: 'text-red-600' };
function Messwerte({ felder, messung }) {
  if (!felder.length && !messung) return <p className="text-base text-muted-foreground">Noch keine Messwerte.</p>;
  return (
    <div data-testid="messung-werte" className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {felder.map((f) => (
          <div key={f.key} className="rounded-2xl border border-border p-4" data-testid={`messwert-${f.key}`}>
            <p className="text-sm text-muted-foreground">{f.label}{f.quelle === 'hand' ? ' · von Hand' : ''}</p>
            <p className="text-2xl font-bold tabular-nums">{zahl(f.wert)} <span className="text-base font-normal">{f.einheit}</span></p>
            {f.delta != null ? (
              <p className={`text-sm tabular-nums ${WERTUNG_FARBE[f.wertung] || 'text-muted-foreground'}`}>
                {f.delta > 0 ? '+' : f.delta < 0 ? '−' : '±'}{zahl(Math.abs(f.delta))} {f.einheit}
                <span className="text-muted-foreground"> seit {tagKurz(f.vorher_tag)}</span>
              </p>
            ) : <p className="text-sm text-muted-foreground">erste Messung</p>}
          </div>
        ))}
        {messung && [['Fettmasse', messung.fettmasse_kg, 'kg', 1], ['Grundumsatz (Waage)', messung.grundumsatz_kcal, 'kcal', 0]]
          .filter(([, v]) => v != null).map(([l, v, e, st]) => (
            <div key={l} className="rounded-2xl border border-border p-4"><p className="text-sm text-muted-foreground">{l}</p><p className="text-2xl font-bold tabular-nums">{zahl(v, st)} <span className="text-base font-normal">{e}</span></p></div>
          ))}
      </div>
      {messung?.naechste_messung && <p className="text-base" data-testid="naechste-messung">Nächste Messung in drei Monaten: <b>{tagKurz(messung.naechste_messung)}</b></p>}
    </div>
  );
}

function HandEingabe({ a, onSpeichern, arbeitet }) {
  const [w, setW] = useState({});
  const stellen = a.messwerte?.stellen || [];
  const feld = (key, label, einheit) => (
    <label key={key} className="text-base">{label}<br />
      <span className="flex items-center gap-2">
        <input inputMode="decimal" value={w[key] ?? ''} onChange={(e) => setW({ ...w, [key]: e.target.value })} data-testid={`hand-${key}`}
          className="min-h-[52px] w-28 rounded-xl border border-border bg-background px-3 text-base tabular-nums" />
        <span className="text-sm text-muted-foreground">{einheit}</span>
      </span>
    </label>
  );
  const voll = Object.values(w).some((v) => String(v).trim() !== '');
  return (
    <div className="space-y-3 rounded-2xl border border-border p-4" data-testid="hand-eingabe">
      <p className="text-lg font-semibold">Von Hand</p>
      <p className="text-sm text-muted-foreground">Umfänge in cm, viszerales Fett als Stufe der Waage. Leere Felder bleiben, wie sie sind.</p>
      <div className="flex flex-wrap gap-4">
        {stellen.map((s) => feld(s.wert, s.label, 'cm'))}
        {feld('viszeral', 'Viszerales Fett', 'Stufe')}
      </div>
      <Knopf rot disabled={arbeitet || !voll} onClick={() => {
        const umfaenge = Object.fromEntries(stellen.map((s) => [s.wert, w[s.wert]]).filter(([, v]) => v != null && String(v).trim() !== ''));
        onSpeichern({ umfaenge, ...(String(w.viszeral ?? '').trim() ? { viszeral: w.viszeral } : {}) });
      }}>Messwerte speichern</Knopf>
    </div>
  );
}

function Befund({ a, onSpeichern, arbeitet }) {
  const aus = a.auswahl || {};
  const b = a.befund || {};
  const [ziel, setZiel] = useState(b.hauptziel || a.antworten?.hauptziel || '');
  const [bewertung, setBewertung] = useState(b.bewertung || '');
  const [huerden, setHuerden] = useState(b.huerden || []);
  const [keinPlan, setKeinPlan] = useState(!!b.kein_plan);
  const [text, setText] = useState(b.befund_text || '');
  const bausteine = (aus.textbausteine || []).filter((t) => !t.bewertung || !bewertung || t.bewertung === bewertung);
  return (
    <section className="space-y-5" data-testid="schritt-befund">
      <h2 className="text-2xl font-bold">Befund & Ziel</h2>
      <div className="space-y-2"><p className="text-lg font-semibold">Hauptziel</p>
        <div className="flex flex-wrap gap-2">{(aus.hauptziele || []).map((o) => <Chip key={o.wert} an={ziel === o.wert} onClick={() => setZiel(o.wert)}>{o.label}</Chip>)}</div></div>
      <div className="space-y-2"><p className="text-lg font-semibold">Bewertung *</p>
        <div className="flex flex-wrap gap-2">{(aus.bewertungen || []).map((o) => <Chip key={o.wert} an={bewertung === o.wert} onClick={() => setBewertung(o.wert)} data-testid={`bewertung-${o.wert}`}>{o.label}</Chip>)}</div></div>
      <div className="space-y-2"><p className="text-lg font-semibold">Hürde des Kunden</p>
        <div className="flex flex-wrap gap-2">{(aus.huerden || []).map((o) => {
          const an = huerden.includes(o.wert);
          return <Chip key={o.wert} an={an} onClick={() => setHuerden(an ? huerden.filter((x) => x !== o.wert) : [...huerden, o.wert])}>{o.label}</Chip>;
        })}</div></div>
      <div className="space-y-2"><p className="text-lg font-semibold">Trainingsplan</p>
        <Chip an={keinPlan} onClick={() => setKeinPlan(!keinPlan)}>Hat keinen Plan / ist unsicher</Chip></div>
      <div className="space-y-2"><p className="text-lg font-semibold">Kurzer Befund</p>
        {bausteine.length > 0 && (
          <div className="flex flex-wrap gap-2">{bausteine.map((t) => (
            <button key={t.text} type="button" className="min-h-[44px] px-3 rounded-xl border border-dashed border-border text-sm text-left"
              onClick={() => setText((x) => (x ? `${x.trim()} ${t.text}` : t.text))}>+ {t.text}</button>
          ))}</div>
        )}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000} data-testid="befund-text"
          className="w-full rounded-xl border border-border bg-background p-3 text-base" />
        <p className="text-sm text-muted-foreground">Kann Gesundheitsangaben enthalten – im CRM nur mit dem Recht Training oder Ernährung sichtbar.</p></div>
      <Knopf rot disabled={arbeitet || !bewertung || !ziel} onClick={() => onSpeichern({ hauptziel: ziel, bewertung, huerden, befund_text: text, kein_plan: keinPlan })}>Befund speichern</Knopf>
    </section>
  );
}

function preisZeile(k) {
  if (k.abrechnung === 'monatlich') return `${euro(k.preis)} je Monat${k.mindestlaufzeit_monate ? `, mind. ${k.mindestlaufzeit_monate} Monate` : ''}`;
  if (k.abrechnung === 'monatlich_n') return `${euro(k.preis)} je Monat · ${k.monate} Monatsrechnungen`;
  return `${euro(k.preis)} einmalig${k.raten_moeglich ? ' · in zwei Raten möglich' : ''}`;
}

/* Gratistage Ernährung zur Auswahl (Coach-Cockpit W1): Bonus einer Angebotsvorlage (PT-Monat), sonst das Aufbau-Paket (Bestand).
   Gerechnet wird nichts — beide Zahlen kommen vom Server. */
function gratisZurAuswahl(a, auswahl) {
  const karten = a.angebotskarten?.karten || [];
  const bonus = karten.filter((k) => auswahl.includes(k.schluessel)).map((k) => k.gratis_ernaehrung_tage).find(Boolean);
  if (bonus) return bonus;
  return auswahl.some((s) => s.startsWith('aufbau_')) ? a.abschluss_info?.gratis_tage_bei_aufbau || null : null;
}

const ART_LABEL = { erst: 'Erst-Check-up', start: 'Start-Check-up', ergebnis: 'Ergebnis-Check-up', abschluss: 'Abschluss-Check-up' };

/* Art des Check-ups (W1.5): vom Server aus der Beschleunigungsphase vorgeschlagen, bis zum Abschluss änderbar. */
function ArtWahl({ a, onArt, arbeitet }) {
  if (!a.arten || !onArt) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="checkup-art">
      <span className="text-sm text-muted-foreground">Art:</span>
      {a.arten.map((k) => (
        <Chip key={k} an={a.art === k} disabled={arbeitet} onClick={() => a.art !== k && onArt(k)} data-testid={`art-${k}`}
          className="min-h-[44px] text-sm px-3">{ART_LABEL[k] || k}{a.art_vorschlag === k && a.art !== k ? ' · Vorschlag' : ''}</Chip>
      ))}
    </div>
  );
}

function Empfehlung({ a, auswahl, setAuswahl, onWeiter }) {
  const ak = useContext(Akzent);
  const emp = a.empfehlung || {};
  const empfohlen = new Set((emp.produkte || []).map((p) => p.schluessel));
  const karten = a.angebotskarten?.karten || [];
  const pakete = karten.filter((k) => k.schluessel !== 'ernaehrung');
  const ern = karten.find((k) => k.schluessel === 'ernaehrung');
  const gratis = gratisZurAuswahl(a, auswahl);
  const paketWaehlen = (s) => setAuswahl((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x.filter((y) => y === 'ernaehrung'), s]));
  return (
    <section className="space-y-4" data-testid="schritt-empfehlung">
      <h2 className="text-2xl font-bold">Empfehlung</h2>
      {emp.begruendung && <p className="text-lg rounded-2xl border border-border p-4" data-testid="empfehlung-begruendung">{emp.begruendung}</p>}
      {!empfohlen.size && emp.regel && <p className="text-base text-muted-foreground">Keine Empfehlung – nächster Check-up in {emp.naechster_checkup_tage} Tagen. Ein Angebot lässt sich trotzdem wählen.</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {pakete.map((k) => {
          const an = auswahl.includes(k.schluessel);
          return (
            <button key={k.schluessel} type="button" aria-pressed={an} onClick={() => paketWaehlen(k.schluessel)} data-testid={`karte-${k.schluessel}`}
              className={`text-left rounded-2xl border-2 p-4 min-h-[44px] space-y-1 ${an ? ak.rahmen : 'border-border'} ${empfohlen.has(k.schluessel) ? 'shadow-md' : ''}`}
              style={an ? ak.rahmenStyle : undefined}>
              <span className="flex items-center justify-between gap-2">
                <span className="text-lg font-bold">{k.titel}</span>
                {empfohlen.has(k.schluessel) && <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ak.className}`} style={ak.style}>Empfohlen</span>}
              </span>
              {k.label && <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full border border-border" data-testid={`label-${k.schluessel}`}>{k.label}</span>}
              <span className="block text-sm text-muted-foreground">{k.kurz}</span>
              {k.vergleichspreis != null && <span className="block text-sm text-muted-foreground tabular-nums"><s>{euro(k.vergleichspreis)}</s> je Monat bei Einzelbuchung</span>}
              <span className="block text-base font-semibold tabular-nums">{preisZeile(k)}</span>
              {k.ersparnis ? <span className="block text-sm font-semibold">Du sparst {euro(k.ersparnis)} gegenüber Einzelbuchung</span> : null}
              {k.gratis_ernaehrung_tage ? <span className="block text-sm text-muted-foreground">Ernährungscoaching {k.gratis_ernaehrung_tage} Tage inklusive</span> : null}
              {k.gym_inklusive && <span className="block text-sm text-muted-foreground">Gym während der Laufzeit inklusive</span>}
              {an && <span className="flex items-center gap-1 text-sm font-semibold"><Check className="w-4 h-4" /> gewählt</span>}
            </button>
          );
        })}
      </div>
      {ern && (
        <button type="button" aria-pressed={auswahl.includes('ernaehrung')} data-testid="karte-ernaehrung"
          onClick={() => setAuswahl((x) => (x.includes('ernaehrung') ? x.filter((y) => y !== 'ernaehrung') : [...x, 'ernaehrung']))}
          className={`w-full min-h-[56px] rounded-xl border-2 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-left ${auswahl.includes('ernaehrung') ? ak.rahmen : 'border-border'}`}
          style={auswahl.includes('ernaehrung') ? ak.rahmenStyle : undefined}>
          <span className="text-base font-semibold">{auswahl.includes('ernaehrung') ? '✓ ' : '+ '}{ern.titel}{empfohlen.has('ernaehrung') ? ' · empfohlen' : ''}</span>
          <span className="text-sm tabular-nums">{gratis ? `erster Monat gratis (${gratis} Tage), danach ${preisZeile(ern)}` : preisZeile(ern)}</span>
        </button>
      )}
      <div className="flex flex-wrap gap-3">
        <Knopf rot onClick={onWeiter} data-testid="weiter-abschluss">{auswahl.length ? 'Weiter zum Abschluss' : 'Ohne Angebot weiter'}</Knopf>
      </div>
    </section>
  );
}

function Abschluss({ a, auswahl, arbeitet, onAbschluss, onZurueck, uebung = false, labels = {} }) {
  const info = a.abschluss_info || {};
  const gruende = Object.fromEntries((a.auswahl?.gruende || []).map((g) => [g.wert, g.label]));
  const karten = (a.angebotskarten?.karten || []).filter((k) => auswahl.includes(k.schluessel));
  const [ausgang, setAusgang] = useState(auswahl.length ? 'buchen' : 'spaeter');
  const [start, setStart] = useState({ datum: heuteIso(1), uhrzeit: '' });
  const [bild, setBild] = useState(null);
  const [haken, setHaken] = useState(false);
  const [mail, setMail] = useState(false);
  const [grund, setGrund] = useState('');
  const [ewHaken, setEwHaken] = useState(false);
  const [eltern, setEltern] = useState('');
  const mitPaket = karten.some((k) => k.schluessel !== 'ernaehrung');
  // Ernährungscoaching oder Aufbau-Paket (Gratismonat): die Einwilligung in die Gesundheitsdaten (E19) gehört dazu
  const ew = info.ernaehrung_einwilligung;
  const gratis = gratisZurAuswahl(a, auswahl);
  const mitInklusive = karten.some((k) => (k.inkl_bausteine || []).includes('ernaehrung'));
  const ewNoetig = ausgang === 'buchen' && !!ew && !ew.erteilt && (auswahl.includes('ernaehrung') || !!gratis || mitInklusive);
  const testKarte = karten.find((k) => k.testtermin_nach_tagen);
  const ewPflicht = ewNoetig && auswahl.includes('ernaehrung');
  const elternOk = !ewNoetig || !ew.minderjaehrig || !!eltern.trim();
  const ewOk = ewPflicht ? ewHaken && elternOk : (!ewHaken || elternOk);
  const bereit = ausgang === 'buchen' ? karten.length && bild && haken && ewOk && (!start.uhrzeit || start.datum)
    : ausgang === 'senden' ? karten.length > 0 : !!grund;
  const senden = () => onAbschluss({
    ausgang, schluessel: ['buchen', 'senden'].includes(ausgang) ? auswahl : null,
    start: ausgang === 'buchen' && mitPaket && start.datum && start.uhrzeit ? start : null,
    unterschrift: ausgang === 'buchen' ? {
      png_base64: bild, bedingungen_fassung: info.bedingungen_fassung,
      ...(ewNoetig && ewHaken ? { einwilligung_ernaehrung: true, einwilligung_version: ew.version, eltern_name: ew.minderjaehrig ? eltern.trim() : undefined } : {}),
    } : null,
    grund: ['spaeter', 'kein_bedarf'].includes(ausgang) ? grund : null, kanal_email: ausgang === 'senden' && mail,
  });
  return (
    <section className="space-y-5" data-testid="schritt-abschluss">
      <h2 className="text-2xl font-bold">Abschluss</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {AUSGAENGE.map(([k, l]) => (
          <Chip key={k} an={ausgang === k} disabled={['buchen', 'senden'].includes(k) && !karten.length} onClick={() => { setAusgang(k); setGrund(''); }}
            data-testid={`ausgang-${k}`} className="disabled:opacity-40">{labels[k] || l}</Chip>
        ))}
      </div>
      {['buchen', 'senden'].includes(ausgang) && (
        <ul className="rounded-2xl border border-border divide-y" data-testid="abschluss-zusammenfassung">
          {karten.map((k) => (
            <li key={k.schluessel} className="p-3 flex justify-between gap-3 text-base">
              <span>{k.titel}</span><span className="tabular-nums">{k.schluessel === 'ernaehrung' && gratis ? 'erster Monat gratis' : preisZeile(k)}</span>
            </li>
          ))}
        </ul>
      )}
      {!karten.length && <p className="text-sm text-muted-foreground">Kein Angebot gewählt – <button type="button" className="underline min-h-[44px]" onClick={onZurueck}>zurück zu{labels.zurueck || 'r Empfehlung'}</button>.</p>}

      {ausgang === 'buchen' && (
        <div className="space-y-4">
          {mitPaket && (
            <div className="space-y-2">
              <p className="text-lg font-semibold">Starttermin (erster Termin)</p>
              <div className="flex flex-wrap gap-4 items-end">
                <label className="text-base">Tag<br /><input type="date" value={start.datum} min={heuteIso()} onChange={(e) => setStart({ ...start, datum: e.target.value })} className="min-h-[52px] rounded-xl border border-border bg-background px-3 text-base" data-testid="start-datum" /></label>
                <label className="text-base">Uhrzeit<br /><input type="time" value={start.uhrzeit} step={900} onChange={(e) => setStart({ ...start, uhrzeit: e.target.value })} className="min-h-[52px] rounded-xl border border-border bg-background px-3 text-base" data-testid="start-uhrzeit" /></label>
              </div>
              <p className="text-sm text-muted-foreground">Ohne Uhrzeit vereinbart das Büro den ersten Termin.</p>
              {testKarte && start.datum && start.uhrzeit && (
                <p className="text-sm" data-testid="testtermin-hinweis">Testtermin nach {testKarte.testtermin_nach_tagen} Tagen: {tagKurz(new Date(new Date(`${start.datum}T12:00:00`).getTime() + testKarte.testtermin_nach_tagen * 864e5).toISOString().slice(0, 10))} um {start.uhrzeit} Uhr, gleicher Trainer (verschiebbar).</p>
              )}
            </div>
          )}
          <p className="text-base">Bitte das Gerät jetzt an das Mitglied weitergeben.</p>
          <Unterschrift wert={bild} onChange={setBild} label="Unterschrift des Mitglieds" tid="abschluss-unterschrift" hoehe={180} />
          <label className="flex items-start gap-3 text-base min-h-[44px]">
            <input type="checkbox" checked={haken} onChange={(e) => setHaken(e.target.checked)} className="mt-1 h-6 w-6" data-testid="bedingungen-haken" />
            <span>Ich habe die <a href={info.bedingungen_url || '/agb#coaching'} target="_blank" rel="noreferrer" className="underline">Bedingungen des P1 Coachings</a> (Fassung {info.bedingungen_fassung}) gelesen und buche verbindlich.</span>
          </label>
          {ewNoetig && (
            <div className="space-y-2 rounded-2xl border border-border p-4" data-testid="einwilligung-ernaehrung">
              <p className="text-base font-semibold">{ew.titel}</p>
              <div className="max-h-48 overflow-y-auto text-sm leading-relaxed whitespace-pre-line">{ew.text}</div>
              {ew.minderjaehrig && (
                <label className="block text-base">Name des Erziehungsberechtigten, der einwilligt *<br />
                  <input value={eltern} onChange={(e) => setEltern(e.target.value)} className="mt-1 w-full min-h-[52px] rounded-xl border border-border bg-background px-3 text-base" data-testid="einwilligung-eltern" />
                </label>
              )}
              <label className="flex items-start gap-3 text-base min-h-[44px]">
                <input type="checkbox" checked={ewHaken} onChange={(e) => setEwHaken(e.target.checked)} className="mt-1 h-6 w-6" data-testid="einwilligung-ernaehrung-haken" />
                <span>{ew.minderjaehrig ? 'Ich willige als Erziehungsberechtigte/r ein.' : 'Ich willige ein.'}</span>
              </label>
              {!ewPflicht && <p className="text-sm text-muted-foreground">Ohne Einwilligung entfällt nur das inklusive Ernährungscoaching.</p>}
            </div>
          )}
        </div>
      )}
      {ausgang === 'senden' && (
        <div className="space-y-2">
          <p className="text-base">Das Angebot erscheint in der App des Mitglieds und gilt {info.angebot_tage || 7} Tage. Es nimmt dort an und unterschreibt im Gerät.</p>
          <label className="flex items-center gap-3 text-base min-h-[44px]"><input type="checkbox" checked={mail} onChange={(e) => setMail(e.target.checked)} className="h-6 w-6" /> zusätzlich per E-Mail</label>
        </div>
      )}
      {['spaeter', 'kein_bedarf'].includes(ausgang) && (
        <div className="space-y-2">
          <p className="text-lg font-semibold">Grund *</p>
          <div className="flex flex-wrap gap-2">{GRUENDE_JE_AUSGANG[ausgang].map((g) => <Chip key={g} an={grund === g} onClick={() => setGrund(g)} data-testid={`grund-${g}`}>{gruende[g] || g}</Chip>)}</div>
          <p className="text-sm text-muted-foreground">{ausgang === 'spaeter' ? `Der Vertrieb fasst in ${info.nachfassen_tage || 3} Tagen nach (Aufgabe).` : 'Es entsteht keine Aufgabe, der Grund wird gespeichert.'}</p>
        </div>
      )}
      <Knopf rot disabled={arbeitet || !bereit} onClick={senden} data-testid="abschluss-senden">
        {labels[`knopf_${ausgang}`] || { buchen: 'Verbindlich buchen', senden: 'Angebot senden', spaeter: 'Später – Check-up abschließen', kein_bedarf: 'Kein Bedarf – Check-up abschließen' }[ausgang]}
      </Knopf>
      <p className="text-sm text-muted-foreground">{uebung
        ? 'Übung: Es wird nichts gebucht, gesendet, als Aufgabe angelegt oder gezählt – nur der Übungsfall steht danach auf fertig.'
        : 'Erst jetzt gilt der Check-up als abgeschlossen und zählt aus dem Kontingent.'}</p>
    </section>
  );
}

/* Ergebnis-Report als PDF (CRM und /coach; der Server kennzeichnet einen nicht abgeschlossenen als Vorschau) */
function PdfKnopf({ client }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');
  const oeffnen = async () => {
    setLaeuft(true); setFehler('');
    try { const r = await client.pdf(); const u = URL.createObjectURL(r.data); window.open(u, '_blank', 'noopener'); setTimeout(() => URL.revokeObjectURL(u), 60000); }
    catch (e) { setFehler(fehlerText(e)); } finally { setLaeuft(false); }
  };
  return (
    <div className="space-y-1">
      <Knopf onClick={oeffnen} disabled={laeuft} data-testid="ergebnis-pdf">{laeuft ? <Loader2 className="inline w-5 h-5 animate-spin" /> : 'Ergebnis als PDF'}</Knopf>
      <p className="text-sm text-muted-foreground">Der Kunde findet das PDF auch in seiner App.</p>
      {fehler && <p className="text-sm text-red-600">{fehler}</p>}
    </div>
  );
}

const FERTIG_TEXT = { buchen: 'Gebucht', senden: 'Angebot gesendet', spaeter: 'Nachfassen vorgemerkt', kein_bedarf: 'Kein Bedarf vermerkt' };

/* Der Stepper. `client`: {ablauf, einwilligung, messung, messwerte, antworten, kurse, befund, empfehlung, abschluss}. */
export default function CheckupStepper({ client, trainerName, onZurueck, zurueckText = 'Liste', crm = false }) {
  const [a, setA] = useState(null);
  const [fehler, setFehler] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [scan, setScan] = useState(false);
  const [analyseGesehen, setAnalyseGesehen] = useState(false);
  const [ohneMessung, setOhneMessung] = useState(false);
  const [nochmalFragen, setNochmalFragen] = useState(false);
  const [handZeigen, setHandZeigen] = useState(false);
  const [zurBefund, setZurBefund] = useState(false);
  const [zumAbschluss, setZumAbschluss] = useState(false);
  const [werte, setWerte] = useState({});
  const [ergebnis, setErgebnis] = useState(null);           // Antwort des Abschlusses
  const [gewaehlt, setGewaehlt] = useState(null);           // Kurstermine zur Auswahl (Objekte)
  const [tausche, setTausche] = useState(null);
  const [auswahl, setAuswahl] = useState(null);             // gewählte Angebotskarten (Schlüssel)
  const empfehlungLaeuft = useRef(false);

  const laden = useCallback(async () => {
    try { const r = (await client.ablauf()).data; setA(r); setWerte((w) => (Object.keys(w).length ? w : (r.antworten || {}))); setFehler(''); }
    catch (e) { setFehler(fehlerText(e)); }
  }, [client]);
  useEffect(() => { laden(); }, [laden]);

  const empfohlen = a?.kursempfehlung?.erstellt_am;
  useEffect(() => { if (a?.kursempfehlung) { setGewaehlt(a.kursempfehlung.uebernommen?.termine || a.kursempfehlung.termine || []); setTausche(null); } }, [empfohlen]); // eslint-disable-line react-hooks/exhaustive-deps
  const empfehlungAm = a?.empfehlung?.am;
  useEffect(() => { if (a) setAuswahl((a.empfehlung?.produkte || []).map((p) => p.schluessel)); }, [empfehlungAm]); // eslint-disable-line react-hooks/exhaustive-deps

  const tu = async (fn, danach, neuLaden = true) => {
    setArbeitet(true); setFehler('');
    try { const r = await fn(); if (danach) danach(r); if (neuLaden) await laden(); return true; }
    catch (e) { setFehler(fehlerText(e)); return false; }
    finally { setArbeitet(false); }
  };

  const onCode = useCallback((code) => {
    setScan(false);
    tu(() => client.messung(code), () => setAnalyseGesehen(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // Schritt bestimmen (Stand vom Server, lokale Merker nur für Ansichten ohne Speicherung)
  let schritt = null;
  if (a) {
    const ka = a.koerperanalyse;
    const hand = !!a.messwerte?.hand;
    const antwortenDa = ['plan', 'freigabe', 'fertig'].includes(a.schritt);
    // Abschluss-Check-up: die Kurse kommen aus „Dein Weg ab jetzt“ (Kurs-Zuordnung, freie Plätze), nicht aus den Antworten
    const kurseOffen = !!a.kursempfehlung && !a.kursempfehlung.uebernommen && a.art !== 'abschluss';
    if (ergebnis || a.checkup.status === 'erledigt' || a.ergebnis) schritt = 'fertig';
    else if (nochmalFragen) schritt = 'fragen';
    else if (a.schritt === 'einwilligung') schritt = 'einwilligung';
    else if (handZeigen || (a.schritt === 'messung' && !ohneMessung && !hand)) schritt = 'messung';
    else if (!antwortenDa && (ka.messung || hand) && !analyseGesehen) schritt = 'analyse';
    else if (!antwortenDa) schritt = 'fragen';
    else if (!a.befund || zurBefund) schritt = 'befund';
    else if (kurseOffen) schritt = 'kurse';
    else if (!zumAbschluss) schritt = 'empfehlung';
    else schritt = 'abschluss';
  }

  // Empfehlung beim Betreten des Schritts einmal rechnen lassen (Regeln der Einrichtung, auf dem Server)
  useEffect(() => {
    if (schritt !== 'empfehlung' || !a || a.empfehlung || empfehlungLaeuft.current || MIT_ERGEBNIS.includes(a.art)) return;
    empfehlungLaeuft.current = true;
    tu(() => client.empfehlung()).finally(() => { empfehlungLaeuft.current = false; });
  }, [schritt, a?.empfehlung]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!a) return <div className="py-16 flex justify-center">{fehler ? <p className="text-red-600">{fehler}</p> : <Loader2 className="w-8 h-8 animate-spin" />}</div>;

  const ka = a.koerperanalyse;
  const fragen = a.fragen.filter((f) => sichtbar(f, werte));
  const speichern = () => tu(() => client.antworten(Object.fromEntries(
    fragen.map((f) => [f.key, werte[f.key]]).filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && !v.length)))),
  () => setNochmalFragen(false));
  const alternativen = [...(a.kursempfehlung?.alternativen || []), ...(a.kursempfehlung?.termine || [])]
    .filter((t, i, arr) => arr.findIndex((x) => terminKey(x) === terminKey(t)) === i)
    .filter((t) => !(gewaehlt || []).some((g) => terminKey(g) === terminKey(t)));
  const ausgangFertig = ergebnis?.ergebnis?.ausgang || a.ergebnis?.ausgang;
  const roh = ergebnis?.meldungen || ergebnis?.ergebnis?.folgen?.meldungen;     // Buchung: Meldungen aus checkup_abschluss.buchen
  const meldungen = Array.isArray(roh) ? roh.filter((m) => typeof m === 'string') : [];

  return (
    <Akzent.Provider value={crm ? AKZENT_CRM : AKZENT_TABLET}>
      <div className="space-y-5" data-testid="checkup-ablauf">
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={onZurueck} className="flex items-center gap-1 text-base min-h-[48px]">
            {crm ? <X className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />} {zurueckText}</button>
          <p className="text-base text-right"><span className="text-muted-foreground">Check-up mit</span> <b>{a.mitglied.vorname}</b><br />
            <span className="text-muted-foreground">Durchgeführt von:</span> <b data-testid="ablauf-trainer">{a.durchgefuehrt_von?.name || trainerName}</b></p>
        </div>
        <Fortschritt schritt={schritt} art={a.art} />
        {schritt !== 'fertig' && <ArtWahl a={a} arbeitet={arbeitet} onArt={client.art ? (k) => tu(() => client.art(k)) : null} />}
        {a.uebung && (
          <p className="rounded-xl border border-amber-400 bg-amber-50 text-amber-900 p-3 text-base" data-testid="uebung-ausgangslage">
            <b>Übungsfall:</b> {a.mitglied.vorname}, {a.uebung.alter} Jahre · {a.mitglied.tarif}{a.mitglied.gym_zugang ? ' mit Gym' : ' ohne Gym'}<br />{a.uebung.ausgangslage}
          </p>
        )}
        {fehler && <p className="rounded-xl bg-red-50 text-red-700 p-3 text-base" role="alert" data-testid="checkup-fehler">{fehler}</p>}

        {schritt === 'einwilligung' && (
          <section className="space-y-4">
            <h2 className="text-2xl font-bold">{ka.einwilligung.titel || 'Einwilligung zur Körperanalyse'}</h2>
            <p className="text-sm text-muted-foreground">Bitte das Gerät jetzt an das Mitglied weitergeben.</p>
            <div className="rounded-2xl border border-border p-5 text-lg leading-relaxed whitespace-pre-line" data-testid="einwilligung-text">{ka.einwilligung.text}</div>
            <div className="flex flex-wrap gap-3">
              <Knopf rot disabled={arbeitet} onClick={() => tu(() => client.einwilligung({ version: ka.einwilligung.version, bestaetigt: true }))}>Mitglied stimmt zu</Knopf>
              <Knopf disabled={arbeitet} onClick={() => tu(() => client.einwilligung({ version: ka.einwilligung.version, bestaetigt: false }))}>Ohne Körperanalyse weiter</Knopf>
            </div>
          </section>
        )}

        {schritt === 'messung' && (
          <section className="space-y-4">
            <h2 className="text-2xl font-bold">Messung</h2>
            {scan ? <Scanner onCode={onCode} onAbbruch={() => setScan(false)} /> : (
              <>
                <div className="flex flex-wrap gap-3">
                  <Knopf rot disabled={arbeitet} onClick={() => setScan(true)}><Camera className="inline w-5 h-5 mr-2" />{ka.messung ? 'Waage neu scannen' : 'Waage scannen'}</Knopf>
                  {handZeigen
                    ? <Knopf onClick={() => setHandZeigen(false)}>Zurück zur Analyse</Knopf>
                    : <Knopf onClick={() => setOhneMessung(true)}>Ohne Messung weiter</Knopf>}
                </div>
                {a.messwerte?.hand_moeglich && <HandEingabe a={a} arbeitet={arbeitet}
                  onSpeichern={(d) => tu(() => client.messwerte(d), () => { setHandZeigen(false); setAnalyseGesehen(false); })} />}
              </>
            )}
          </section>
        )}

        {schritt === 'analyse' && (
          <section className="space-y-4">
            <h2 className="text-2xl font-bold">Analyse</h2>
            {scan ? <Scanner onCode={onCode} onAbbruch={() => setScan(false)} /> : (
              <>
                <Messwerte felder={a.messwerte?.felder || []} messung={ka.messung} />
                <div className="flex flex-wrap gap-3">
                  <Knopf rot onClick={() => setAnalyseGesehen(true)}>Weiter zu den Zielen</Knopf>
                  <Knopf onClick={() => setScan(true)}>{ka.messung ? 'Noch einmal scannen' : 'Waage scannen'}</Knopf>
                  {a.messwerte?.hand_moeglich && <Knopf onClick={() => setHandZeigen(true)}>Werte von Hand</Knopf>}
                </div>
              </>
            )}
          </section>
        )}

        {schritt === 'fragen' && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Ziele und Training</h2>
            {fragen.map((f) => <Frage key={f.key} f={f} wert={werte[f.key]} setWert={(v) => setWerte((w) => ({ ...w, [f.key]: v }))} />)}
            <Knopf rot disabled={arbeitet} onClick={speichern}>Antworten speichern</Knopf>
          </section>
        )}

        {schritt === 'befund' && <Befund a={a} arbeitet={arbeitet} onSpeichern={(d) => tu(() => client.befund(d), () => { setZurBefund(false); setZumAbschluss(false); })} />}

        {schritt === 'kurse' && (
          <section className="space-y-4" data-testid="kursempfehlung">
            <h2 className="text-2xl font-bold">Kursempfehlung</h2>
            <p className="text-base text-muted-foreground">Gebucht wird hier nichts — das Mitglied bucht die übernommenen Kurse in seiner App.</p>
            {(gewaehlt || []).length === 0 && <p className="text-base">Keine Kurstermine empfohlen.</p>}
            {(gewaehlt || []).map((t, i) => (
              <div key={terminKey(t)} className="rounded-2xl border border-border p-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-lg"><b>{t.tag_lang || t.tag} {t.start}</b> · {t.name}{t.grund ? <span className="block text-sm text-muted-foreground">{t.grund}</span> : null}</p>
                  <Knopf onClick={() => setTausche(tausche === i ? null : i)}>Anderen Termin</Knopf>
                </div>
                {tausche === i && (
                  <div className="flex flex-wrap gap-2">
                    {alternativen.length === 0 ? <p className="text-sm text-muted-foreground">Keine Alternative vorhanden.</p> : alternativen.map((x) => (
                      <button key={terminKey(x)} type="button" className="min-h-[48px] px-4 rounded-xl border border-border text-base"
                        onClick={() => { setGewaehlt((g) => g.map((y, j) => (j === i ? x : y))); setTausche(null); }}>{x.tag} {x.start} · {x.name}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {(a.kursempfehlung?.hinweise || []).map((h, i) => <p key={i} className="text-sm text-muted-foreground">{h}</p>)}
            <div className="flex flex-wrap gap-3">
              <Knopf rot disabled={arbeitet} onClick={() => tu(() => client.kurse((gewaehlt || []).map(terminKey)))}>Passt – übernehmen</Knopf>
              <Knopf disabled={arbeitet} onClick={() => setNochmalFragen(true)}>Neu empfehlen</Knopf>
            </div>
          </section>
        )}

        {schritt === 'empfehlung' && MIT_ERGEBNIS.includes(a.art) && (client.ergebnis ? (
          <>
            {a.art === 'ergebnis'
              ? <ErgebnisSeite client={client} ak={crm ? AKZENT_CRM : AKZENT_TABLET} onWeiter={(aus) => { setAuswahl(aus); setZumAbschluss(true); }} />
              : <WegSeite client={client} ak={crm ? AKZENT_CRM : AKZENT_TABLET} crm={crm} onWeiter={(aus) => { setAuswahl(aus); setZumAbschluss(true); }} />}
            <button type="button" className="text-base underline min-h-[44px]" onClick={() => setZurBefund(true)}>Befund ändern</button>
          </>
        ) : <p className="text-base text-muted-foreground">Ergebnis- und Abschluss-Check-up gibt es hier nicht – bitte im CRM, auf /coach oder am Studio-Tablet.</p>)}

        {schritt === 'empfehlung' && !MIT_ERGEBNIS.includes(a.art) && (
          !a.empfehlung || !auswahl ? <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div> : (
            <>
              <Empfehlung a={a} auswahl={auswahl} setAuswahl={setAuswahl} onWeiter={() => setZumAbschluss(true)} />
              <button type="button" className="text-base underline min-h-[44px]" onClick={() => setZurBefund(true)}>Befund ändern</button>
            </>
          )
        )}

        {schritt === 'abschluss' && (
          <Abschluss a={a} auswahl={auswahl || []} arbeitet={arbeitet} uebung={!!a.uebung} onZurueck={() => setZumAbschluss(false)}
            labels={a.art === 'abschluss' ? { kein_bedarf: 'Ohne weitere Buchung', knopf_kein_bedarf: 'Check-up abschließen', zurueck: ' „Dein Weg“' }
              : a.art === 'ergebnis' ? { zurueck: 'm Ergebnis' } : {}}
            onAbschluss={(d) => tu(() => client.abschluss(d), (r) => setErgebnis({ ...(r.data || {}), ergebnis: { ausgang: d.ausgang, ...(r.data?.ergebnis || {}) } }), false)} />
        )}

        {schritt === 'fertig' && (
          <section className="space-y-4">
            <h2 className="text-2xl font-bold" data-testid="checkup-fertig">Check-up abgeschlossen{ausgangFertig ? ` · ${FERTIG_TEXT[ausgangFertig] || ''}` : ''}</h2>
            {meldungen.map((m, i) => <p key={i} className="text-base">{m}</p>)}
            {MIT_ERGEBNIS.includes(a.art) && !a.uebung && (
              <>
                {/* Testimonial-Frage (W1.7): am Ende des Ergebnis- und des Abschluss-Check-ups; die Karte zeigt den Stand
                    und fragt erneut, wenn bisher abgelehnt oder nur Zahlen */}
                <TestimonialFrage checkupId={a.checkup.id} art={a.art} client={client} />
                {client.pdf && <PdfKnopf client={client} />}
              </>
            )}
            <p className="text-base text-muted-foreground">{a.uebung
              ? `Gut gemacht, ${a.durchgefuehrt_von?.name || trainerName}. Die Übung ist fertig – „Übungen zurücksetzen“ startet sie neu.`
              : `Danke, ${a.durchgefuehrt_von?.name || trainerName}. Der Check-up steht auf dich.`}</p>
            <Knopf rot onClick={onZurueck}>{crm ? 'Schließen' : 'Zur Liste'}</Knopf>
          </section>
        )}
      </div>
    </Akzent.Provider>
  );
}

/* API-Clients: derselbe Stepper, andere Adressen. Der Übungsmodus am Tablet (`checkupUebungAPI`) hat dieselben Aufrufe. */
export function tabletClient(token, trainerId, id, T = checkupTabletAPI) {
  return {
    ablauf: () => T.ablauf(token, trainerId, id),
    einwilligung: (d) => T.einwilligung(token, trainerId, id, d),
    messung: (code) => T.messung(token, trainerId, id, code),
    messwerte: (d) => T.messwerte(token, trainerId, id, d),
    antworten: (antw) => T.antworten(token, trainerId, id, antw),
    kurse: (t) => T.kurse(token, trainerId, id, t),
    befund: (d) => T.befund(token, trainerId, id, d),
    empfehlung: () => T.empfehlung(token, trainerId, id),
    abschluss: (d) => T.abschluss(token, trainerId, id, d),
    art: T.art ? (k) => T.art(token, trainerId, id, k) : null,       // Übungsmodus kennt keine Art
    // w1-ergebnis (Übungsmodus kennt sie nicht)
    ergebnis: T.ergebnis ? () => T.ergebnis(token, trainerId, id) : null,
    einschaetzungVorschlag: T.einschaetzungVorschlag ? () => T.einschaetzungVorschlag(token, trainerId, id) : null,
    einschaetzung: T.einschaetzung ? (d) => T.einschaetzung(token, trainerId, id, d) : null,
    weg: T.weg ? (d) => T.weg(token, trainerId, id, d) : null,
    foto: T.foto ? (fid) => T.foto(token, trainerId, id, fid) : null,
  };
}

export const uebungClient = (token, trainerId, id) => tabletClient(token, trainerId, id, checkupUebungAPI);

export function crmClient(id) {
  const C = checkupAblaufAPI;
  return {
    ablauf: () => C.ablauf(id),
    einwilligung: (d) => C.einwilligung(id, d),
    messung: (code) => C.messung(id, code),
    messwerte: (d) => C.messwerte(id, d),
    antworten: (antw) => C.antworten(id, antw),
    kurse: (t) => C.kurse(id, t),
    befund: (d) => C.befund(id, d),
    empfehlung: () => C.empfehlung(id),
    abschluss: (d) => C.abschluss(id, d),
    art: (k) => C.art(id, k),
    ergebnis: () => C.ergebnis(id),
    einschaetzungVorschlag: () => C.einschaetzungVorschlag(id),
    einschaetzung: (d) => C.einschaetzung(id, d),
    weg: (d) => C.weg(id, d),
    foto: (fid) => C.foto(id, fid),
    pdf: () => C.pdf(id),
  };
}

/* Vollbild-Panel im CRM (Lead-Ansicht; später auch aus dem Kopf der Kundenakte): derselbe Stepper mit dem CRM-Client.
   Der CRM-Nutzer wird auf dem Server über sein Trainer-Profil zum Trainer — ohne Profil kommt der Satz des Servers. */
export function CheckupStepperPanel({ leadId, checkupId, onClose, client: eigener }) {
  // `client`: auf der Trainer-Webseite /coach derselbe Stepper über die Trainer-Sitzung (`/api/coach/checkup-ablauf/…`)
  const [client] = useState(() => eigener || { ...crmClient(checkupId), testimonial: leadId ? testimonialCrm(leadId) : null });
  const zu = useRef(onClose);
  zu.current = onClose;
  useEffect(() => {
    const alt = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = (e) => { if (e.key === 'Escape') zu.current?.(); };
    window.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = alt; window.removeEventListener('keydown', esc); };
  }, []);
  // per Portal an <body>: eine Karte mit Bewegung (transform) wäre sonst der Bezugsrahmen von `fixed`
  return createPortal(
    <div className="fixed inset-0 z-50 bg-background text-foreground overflow-y-auto" role="dialog" aria-modal="true" aria-label="Check-up"
      data-testid="checkup-stepper-panel" data-lead={leadId}>
      <div className="max-w-3xl mx-auto px-4 py-5" style={{ paddingTop: 'max(1.25rem, env(safe-area-inset-top))' }}>
        <CheckupStepper client={client} crm zurueckText="Schließen" onZurueck={() => zu.current?.()} />
      </div>
    </div>,
    document.body,
  );
}
